import React, {useState} from "react";
import {Alert, Box, Dialog, DialogActions, DialogContentText, IconButton} from "@mui/material";
import type {GridFilterInputValueProps} from '@mui/x-data-grid';
import {useGridRootProps} from '@mui/x-data-grid';
import {Sync} from "@mui/icons-material";
import FilterIcon from "~/components/icons/svg/FilterIcon.svg?react";
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {DatePicker} from "@mui/x-date-pickers-pro";
import type {Dayjs} from "dayjs";
import dayjs from "dayjs";

const HTML_ID_PREFIX = 'date-between-filter-';

/**
 * Both bounds are required (mirrors IntegerBetweenFilterComponent): the BE can't handle a
 * one-sided "between" value like [null, date].
 */
function hasExactlyOneBound(lowerBound: Date | undefined, upperBound: Date | undefined): boolean {
    return (lowerBound !== undefined) !== (upperBound !== undefined);
}

function validateInput(lowerBound: Date | undefined, upperBound: Date | undefined): boolean {
    if (!lowerBound || !upperBound) return false;
    return lowerBound.getTime() <= upperBound.getTime();
}

function toDayjs(value: Date | undefined): Dayjs | null {
    return value ? dayjs(value) : null;
}

export const DateBetweenFilterComponent: React.FC<GridFilterInputValueProps> = ({
                                                                                    item,
                                                                                    applyValue,
                                                                                    focusElementRef = null
                                                                                }) => {
    // Taken from https://mui.com/x/react-data-grid/filtering/customization/#multiple-values-operator
    // Modeled after ScoreBetweenFilterComponent.tsx, swapping numeric inputs for date pickers.

    const rootProps = useGridRootProps();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const handleClear = () => {
        setFilterValueState([undefined, undefined]);
        setIsDialogOpen(false);
    }
    const handleClose = () => setIsDialogOpen(false);
    const handleSave = () => {
        if (Array.isArray(filterValueState)) {
            const [lowerBound, upperBound] = filterValueState;
            setIsApplying(true);
            filterTimeout.current = setTimeout(() => {
                setIsApplying(false);
                applyValue({...item, value: [lowerBound, upperBound]});
            }, rootProps.filterDebounceMs);
        }
        setIsDialogOpen(false);
    };

    const filterTimeout = React.useRef<ReturnType<typeof setTimeout>>(undefined);
    const [filterValueState, setFilterValueState] = React.useState<[Date | undefined, Date | undefined]>(item.value ?? [undefined, undefined]);
    const [applying, setIsApplying] = React.useState(false);
    const filterValueStateIsSet = Array.isArray(filterValueState) && filterValueState.length === 2 && (Boolean(filterValueState[0]) || Boolean(filterValueState[1]));

    React.useEffect(() => {
        return () => {
            clearTimeout(filterTimeout.current);
        };
    }, []);

    React.useEffect(() => {
        const itemValue = item.value ?? [undefined, undefined];
        setFilterValueState(itemValue);
    }, [item.value]);

    const updateFilterValue = (lowerBound: Date | undefined, upperBound: Date | undefined) => {
        clearTimeout(filterTimeout.current);
        setFilterValueState([lowerBound, upperBound]);
    };
    const handleLowerFilterChange = (date: Dayjs | null) => {
        updateFilterValue(date ? date.toDate() : undefined, filterValueState[1]);
    };
    const handleUpperFilterChange = (date: Dayjs | null) => {
        updateFilterValue(filterValueState[0], date ? date.toDate() : undefined);
    };

    const isActive = applying || filterValueStateIsSet;

    return <>
        <Dialog open={isDialogOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                aria-labelledby={HTML_ID_PREFIX + "-title"}
                aria-describedby={HTML_ID_PREFIX + "-description"}>
            <DialogTitle id={HTML_ID_PREFIX + "-title"}>
                Filter by date
            </DialogTitle>
            <DialogContent>
                <DialogContentText id={HTML_ID_PREFIX + "-description"} component="div">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                flexDirection: 'row',
                                alignItems: 'end',
                                height: 48,
                                pl: '20px',
                            }}
                        >
                            <DatePicker
                                label="From"
                                value={toDayjs(filterValueState[0])}
                                onChange={handleLowerFilterChange}
                                inputRef={focusElementRef}
                                slotProps={{textField: {variant: 'standard', sx: {mr: 2}}}}
                            />
                            <DatePicker
                                label="To"
                                value={toDayjs(filterValueState[1])}
                                onChange={handleUpperFilterChange}
                                slotProps={{
                                    textField: {
                                        variant: 'standard',
                                        InputProps: applying ? {endAdornment: <Sync/>} : {}
                                    }
                                }}
                            />
                        </Box>
                    </LocalizationProvider>
                    {hasExactlyOneBound(filterValueState[0], filterValueState[1]) && <Alert
                        severity="info"
                        className="m-4"
                    >
                        This filter requires both From and To. If you only need one bound, use the
                        "Greater than" / "Less than" operators instead.
                    </Alert>}
                    {!hasExactlyOneBound(filterValueState[0], filterValueState[1]) &&
                        !validateInput(filterValueState[0], filterValueState[1]) &&
                        (filterValueState[0] !== undefined || filterValueState[1] !== undefined) && <Alert
                            severity="warning"
                            className="m-4"
                    >
                            Invalid filter! Make sure From&nbsp;&le;&nbsp;To
                    </Alert>}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClear}>Clear</Button>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleSave} autoFocus
                        disabled={!validateInput(filterValueState[0], filterValueState[1])}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        <Box sx={{display: 'flex', justifyContent: 'center', width: '100%'}}>
            <IconButton
                size="small" color={isActive ? "primary" : "default"}
                onClick={() => setIsDialogOpen(true)}
            >
                <FilterIcon fontSize="small"/>
            </IconButton>
        </Box>
    </>;

}
